import React from 'react';

/** Nav state derived from route (e.g. active ids). Provided by Platform; when null, layout uses props only. */
export interface FullLayoutNavState {
  appNavActiveId?: string;
  sectionNavActiveId?: string;
  activeArticleId?: string;
}

const FullLayoutNavStateContext = React.createContext<FullLayoutNavState | null>(null);

export { FullLayoutNavStateContext };

export interface FullLayoutContextValue {
  isSideNavOpen: boolean;
  isRightSidebarOpen: boolean;
  toggleSidebar: () => void;
  openRightSidebar: () => void;
  closeRightSidebar: () => void;
  toggleRightSidebar: () => void;
  showSidenav: () => void;
  disableLeftSideNav: boolean;
  leftNavWidth: number;
  isMobile: boolean;
  sideNavRef: React.RefObject<HTMLDivElement | null>;
  rightSidebarRef: React.RefObject<HTMLElement | null>;
  sidebarToggleRef: React.RefObject<HTMLButtonElement | null>;
  handleResizeStart: (e: React.MouseEvent) => void;
  handleResizeKeyDown: (e: React.KeyboardEvent) => void;
  ariaValuenow: number;
  ariaValuemin: number;
  ariaValuemax: number;
  /** Mirrors `FullLayout` prop; `FullLayout.Main` uses it for viewport height when Radix ScrollArea is off. */
  disableMainScrollArea: boolean;
}

const FullLayoutContext = React.createContext<FullLayoutContextValue | null>(null);

export interface FullLayoutProviderProps {
  value: FullLayoutContextValue;
  children: React.ReactNode;
}

export function FullLayoutProvider({ value, children }: FullLayoutProviderProps) {
  return <FullLayoutContext.Provider value={value}>{children}</FullLayoutContext.Provider>;
}

export function useFullLayoutContext(): FullLayoutContextValue | null {
  return React.useContext(FullLayoutContext);
}

export interface UseRightSidebarReturn {
  /** Whether the right sidebar is currently open. */
  isOpen: boolean;
  /** Open the right sidebar (no-op if already open). */
  open: () => void;
  /** Close the right sidebar (no-op if already closed). */
  close: () => void;
  /** Toggle the right sidebar open/closed. */
  toggle: () => void;
}

/**
 * Convenience hook for programmatically controlling the right sidebar.
 *
 * Must be used inside a `<FullLayout>` tree.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const rightSidebar = useRightSidebar();
 *   return <button onClick={rightSidebar.open}>Open sidebar</button>;
 * }
 * ```
 */
export function useRightSidebar(): UseRightSidebarReturn {
  const ctx = React.useContext(FullLayoutContext);
  if (!ctx) {
    throw new Error('useRightSidebar must be used within a <FullLayout> component.');
  }

  return {
    isOpen: ctx.isRightSidebarOpen,
    open: ctx.openRightSidebar,
    close: ctx.closeRightSidebar,
    toggle: ctx.toggleRightSidebar,
  };
}
